import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { MetricCard } from "@/components/Reports/MetricCard";
import { FilterBar } from "@/components/Reports/FilterBar";
import { ExportButton } from "@/components/Reports/ExportButton";
import { WeekdayChart } from "@/components/Reports/WeekdayChart";
import { TrendChart } from "@/components/Reports/TrendChart";
import { StatusChart } from "@/components/Reports/StatusChart";
import { HelpTooltip } from "@/components/HelpTooltip";
import { Gift, Coins, CheckCircle, Clock, BarChart, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { router } from '@inertiajs/react';
import AppLayout from '@/layouts/AppLayout';

export default function RedemptionsReport({
  metrics = {},
  categoryDistribution = [],
  trendData = [],
  statusBreakdown = {},
  topUsers = [],
  recentRedemptions = [],
  filters = {},
  weekdayDistribution = []
}) {
  const handleBackToReports = () => {
    router.visit("/admin?tab=reports");
  };

  const getCategoryBadge = (category) => {
    const configs = {
      gift_card: { label: '🎁 Gift Card', className: 'bg-purple-100 text-purple-700' }
    };
    const config = configs[category] || { label: category, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={cn("text-[10px]", config.className)}>{config.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const configs = {
      sent: { label: 'Enviada', className: 'bg-orange-100 text-orange-700' },
      fulfilled: { label: 'Cumplida', className: 'bg-blue-100 text-blue-700' },
      cancelled: { label: 'Cancelada', className: 'bg-gray-100 text-gray-700' }
    };
    const config = configs[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={cn("text-[10px]", config.className)}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToReports}
              className="text-purple-600 hover:bg-purple-50 -ml-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Volver a Reportes
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-bambu-black flex items-center gap-3">
            <BarChart className="w-8 h-8 text-purple-600" />
            Reporte de Canjes
            <HelpTooltip content={
              <div>
                <p className="font-semibold mb-1">¿Qué es este reporte?</p>
                <p className="mb-2">Monitorea el canje de puntos por recompensas, analiza fulfillment rate, tiempo de procesamiento y preferencias de usuarios.</p>
                <p className="font-semibold mb-1">Verás:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>Total de canjes y puntos gastados</li>
                  <li>Tasa de cumplimiento</li>
                  <li>Tiempo promedio de procesamiento</li>
                  <li>Top usuarios que más canjean</li>
                  <li>Distribución por estado</li>
                </ul>
              </div>
            } />
          </h1>
          <p className="text-gray-500 mt-1">Análisis detallado de redemptions y métricas</p>
        </div>
        <ExportButton filters={filters} />
      </div>

      {/* Filtros */}
      <FilterBar currentFilters={filters} reportPath="/admin/reports/redemptions" />

      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Canjes"
          value={metrics.totalRedemptions || 0}
          icon={Gift}
          color="orange"
          trend={metrics.trends?.redemptionsChange}
          helpText="Cantidad total de redemptions realizadas en el período seleccionado"
        />
        <MetricCard
          title="Puntos Canjeados"
          value={metrics.totalPoints || 0}
          icon={Coins}
          color="green"
          suffix=" pts"
          trend={metrics.trends?.pointsChange}
          helpText="Suma total de puntos gastados en canjes por recompensas"
        />
        <MetricCard
          title="Tasa de Cumplimiento"
          value={metrics.fulfillmentRate || 0}
          icon={CheckCircle}
          color="blue"
          suffix="%"
          helpText="Porcentaje de canjes completados exitosamente vs. total de canjes"
        />
        <MetricCard
          title="Tiempo Promedio"
          value={metrics.avgProcessingTime || 0}
          icon={Clock}
          color="purple"
          suffix=" días"
          helpText="Días promedio para procesar y cumplir un canje de recompensa"
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Distribución por Día de la Semana
              <HelpTooltip content="Identifica qué días de la semana los usuarios realizan más canjes. Útil para planificar inventario y recursos" />
            </CardTitle>
            <p className="text-sm text-gray-500">Canjes por día de la semana</p>
          </CardHeader>
          <CardContent>
            <WeekdayChart data={weekdayDistribution} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Distribución por Estado
              <HelpTooltip content="Muestra cuántos canjes están pendientes (sent), cumplidos (fulfilled) o cancelados. Permite identificar cuellos de botella en el procesamiento" />
            </CardTitle>
            <p className="text-sm text-gray-500">Estado actual de los canjes</p>
          </CardHeader>
          <CardContent>
            <StatusChart data={statusBreakdown} />
          </CardContent>
        </Card>
      </div>

      {/* Evolución Temporal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Evolución Temporal
            <HelpTooltip content="Gráfico que muestra la tendencia de canjes y puntos gastados durante los últimos 6 meses, permitiendo identificar patrones estacionales" />
          </CardTitle>
          <p className="text-sm text-gray-500">Tendencia de canjes en los últimos 6 meses</p>
        </CardHeader>
        <CardContent>
          <TrendChart data={trendData} />
        </CardContent>
      </Card>

      {/* Top 10 Usuarios */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Top 10 Usuarios
            <HelpTooltip content="Lista de usuarios que más han canjeado puntos por recompensas. Identifica a los usuarios más activos en el programa de beneficios" />
          </CardTitle>
          <p className="text-sm text-gray-500">Usuarios con más canjes en el período</p>
        </CardHeader>
        <CardContent>
          {topUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No hay datos de usuarios para mostrar
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3">Posición</th>
                    <th className="px-4 py-3">Usuario</th>
                    <th className="px-4 py-3 text-center">Canjes</th>
                    <th className="px-4 py-3 text-right">Puntos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {topUsers.map((user, index) => (
                    <tr key={user.userId} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
                          index === 0 ? "bg-yellow-100 text-yellow-700" :
                          index === 1 ? "bg-gray-100 text-gray-600" :
                          index === 2 ? "bg-orange-100 text-orange-700" :
                          "bg-purple-50 text-purple-600"
                        )}>
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-xs">
                            {user.fullName?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-800 text-xs">{user.fullName}</div>
                            <div className="text-[10px] text-gray-400">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-bold text-orange-600">{user.redemptionCount}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono font-bold text-green-600 text-xs">
                          {user.totalPoints?.toLocaleString()} pts
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Canjes Recientes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Canjes Recientes</CardTitle>
          <p className="text-sm text-gray-500">Últimos 50 canjes en el período seleccionado</p>
        </CardHeader>
        <CardContent>
          {recentRedemptions.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No hay canjes en el período seleccionado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Usuario</th>
                    <th className="px-4 py-3">Categoría</th>
                    <th className="px-4 py-3 text-right">Puntos</th>
                    <th className="px-4 py-3 text-center">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {recentRedemptions.map((redemption) => (
                    <tr key={redemption.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600">
                          {new Date(redemption.created_at).toLocaleDateString()}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {new Date(redemption.created_at).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-xs">
                            {redemption.user?.full_name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-800 text-xs">
                              {redemption.user?.full_name}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {redemption.user?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getCategoryBadge(redemption.category)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono font-bold text-orange-600 text-xs">
                          {redemption.points_spent} pts
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(redemption.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

RedemptionsReport.layout = page => <AppLayout children={page} />;
